import { useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import type { VoivodeshipOption } from "@/features/rfq/api/scopes";
import {
  areaFilterCounts,
  NO_AREA_KEY,
  toggleCounty,
  toggleVoivodeship,
  type AreaFilter,
} from "@/features/contractors/areaFilter";

const LABEL = "Obszar działania";

/**
 * Filtr „Obszar działania" — drzewko województw z powiatami, w wyglądzie
 * FilterMultiSelect. Zaznaczone województwo rozwija listę SWOICH powiatów; wybór
 * powiatów zawęża filtr w obrębie tego województwa (logika w areaFilter.ts).
 * Szukajka znajduje też powiat niezaznaczonego województwa — zaznaczenie go
 * zaznacza przy okazji województwo.
 */
export function AreaTreeFilter({
  options,
  value,
  onChange,
}: {
  /** Słownik /rfq/voivodeships/ — undefined, póki się ładuje. */
  options: VoivodeshipOption[] | undefined;
  value: AreaFilter;
  onChange: (next: AreaFilter) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Zwinięte ręcznie województwa; świeżo zaznaczone rozwija się samo.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const counts = areaFilterCounts(value);
  const q = query.trim().toLowerCase();

  const expand = (v: string) => setCollapsed((s) => new Set([...s].filter((x) => x !== v)));
  const onVoivodeship = (v: string) => {
    if (!(v in value)) expand(v);
    onChange(toggleVoivodeship(value, v));
  };
  const onCounty = (v: string, teryt: string) => {
    if (!(v in value)) expand(v);
    onChange(toggleCounty(value, v, teryt));
  };
  const toggleCollapsed = (v: string) =>
    setCollapsed((s) => {
      const next = new Set(s);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });

  // Przy szukaniu: województwo po nazwie albo po pasującym powiecie. Powiaty pod
  // zaznaczonym województwem — wszystkie (pasujące, gdy nazwa województwa nie pasuje);
  // pod niezaznaczonym — tylko te znalezione szukajką.
  const rows = (options ?? []).flatMap((option) => {
    const checked = option.value in value;
    const labelHit = !q || option.label.toLowerCase().includes(q);
    const countyHits = q ? option.counties.filter((c) => c.name.toLowerCase().includes(q)) : [];
    if (!labelHit && countyHits.length === 0) return [];
    let counties = countyHits;
    if (checked && !q) counties = collapsed.has(option.value) ? [] : option.counties;
    else if (checked && labelHit) counties = option.counties;
    return [{ option, checked, counties }];
  });
  const noneHit = !q || "bez obszaru działania".includes(q);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs border rounded-md bg-white dark:bg-gray-700 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors ${open ? "border-[var(--color-primary)] ring-1 ring-[var(--color-primary)]" : "border-gray-300"}`}
      >
        <span className="text-gray-700 dark:text-gray-200">{LABEL}</span>
        {counts.voivodeships > 0 && (
          <span
            title="Zaznaczone województwa (z „Bez obszaru działania”)"
            className="bg-[var(--color-primary)] text-white text-[10px] rounded-full px-1.5 leading-4 font-semibold"
          >
            {counts.voivodeships}
          </span>
        )}
        {counts.counties > 0 && (
          <span
            title="Wybrane powiaty"
            className="border border-[var(--color-primary)] text-[var(--color-primary)] text-[10px] rounded-full px-1.5 leading-4 font-semibold"
          >
            {counts.counties} pow.
          </span>
        )}
        <svg
          className={`w-3 h-3 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div
          role="group"
          aria-label={LABEL}
          className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg w-80 max-h-96 overflow-y-auto pb-1"
        >
          {/* Bez górnego paddingu kontenera — inaczej przewijane wiersze prześwitują nad nagłówkiem. */}
          <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 px-2 pt-2 pb-1.5 border-b border-gray-100 dark:border-gray-700">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Szukaj województwa lub powiatu…"
              aria-label="Szukaj województwa lub powiatu"
              autoFocus
              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]"
            />
            <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px]">
              <span className="text-gray-400 dark:text-gray-500">
                Zaznacz województwo, by wybrać powiaty.
              </span>
              {counts.voivodeships > 0 && (
                <button
                  type="button"
                  onClick={() => onChange({})}
                  className="font-medium text-gray-500 dark:text-gray-400 hover:underline"
                >
                  Wyczyść
                </button>
              )}
            </div>
          </div>
          {!options && <p className="px-3 py-2 text-xs text-gray-400">Ładowanie…</p>}
          {options && rows.length === 0 && !noneHit && (
            <p className="px-3 py-2 text-xs text-gray-400">Brak wyników</p>
          )}
          {rows.map(({ option, checked, counties }) => {
            const picked = value[option.value] ?? [];
            const expanded = !collapsed.has(option.value);
            return (
              <div key={option.value}>
                <div className="flex items-center gap-1 pr-2 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <label
                    title={option.label}
                    className="flex min-w-0 flex-1 items-center gap-2 pl-3 py-1.5 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onVoivodeship(option.value)}
                      className="accent-[var(--color-primary)] shrink-0"
                    />
                    <span className="text-xs text-gray-700 dark:text-gray-200 leading-tight truncate">
                      {option.label}
                    </span>
                  </label>
                  {checked && (
                    <span className="shrink-0 text-[11px] text-gray-400 dark:text-gray-500">
                      {picked.length > 0
                        ? `${picked.length} z ${option.counties.length} pow.`
                        : "wszystkie powiaty"}
                    </span>
                  )}
                  {checked && !q && (
                    <button
                      type="button"
                      onClick={() => toggleCollapsed(option.value)}
                      aria-expanded={expanded}
                      aria-label={`${expanded ? "Zwiń" : "Rozwiń"} powiaty: ${option.label}`}
                      className="shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      <ChevronRight size={12} className={`transition-transform ${expanded ? "rotate-90" : ""}`} />
                    </button>
                  )}
                </div>
                {counties.map((county) => (
                  <label
                    key={county.teryt}
                    title={county.name}
                    className="flex items-center gap-2 pl-9 pr-3 py-1 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={picked.includes(county.teryt)}
                      onChange={() => onCounty(option.value, county.teryt)}
                      className="accent-[var(--color-primary)] shrink-0"
                    />
                    <span className="text-xs text-gray-600 dark:text-gray-300 leading-tight truncate">
                      {county.name}
                    </span>
                  </label>
                ))}
              </div>
            );
          })}
          {options && noneHit && (
            <label className="flex items-center gap-2 px-3 py-1.5 mt-1 border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={NO_AREA_KEY in value}
                onChange={() => onChange(toggleVoivodeship(value, NO_AREA_KEY))}
                className="accent-[var(--color-primary)] shrink-0"
              />
              <span className="text-xs text-gray-700 dark:text-gray-200 leading-tight">
                Bez obszaru działania
              </span>
            </label>
          )}
        </div>
      )}
    </div>
  );
}
